import { useState } from "react";
import { Link } from "react-router-dom";

export function DonatePage() {
  const [amount, setAmount] = useState<number | "">("");
  const [customAmount, setCustomAmount] = useState("");
  const [selectedMethod, setSelectedMethod] = useState<"bank" | "zelle" | "upi" | "">("");
  const [copied, setCopied] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const presetAmounts = [11, 21, 51, 108, 251, 501];

  function copyToClipboard(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  const finalAmount = amount !== "" ? amount : customAmount ? Number(customAmount) : 0;

  function handleDonate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="container" style={{ padding: "5rem 0", textAlign: "center" }}>
        <div className="card" style={{ maxWidth: "520px", margin: "0 auto", padding: "3rem 2rem" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🙏</div>
          <h1 className="display-3" style={{ marginBottom: "0.75rem" }}>Thank You!</h1>
          <p className="body-lg text-muted">
            Your generosity supports free teachings and transformative events for seekers
            worldwide. You will receive a confirmation at the email you provided.
          </p>
          <div style={{ marginTop: "2rem", display: "flex", gap: "1rem", justifyContent: "center" }}>
            <Link to="/" className="btn btn--brand">Return Home</Link>
            <Link to="/events" className="btn btn--outline">See Events</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className="page-hero page-hero--brand">
        <div className="container container--narrow" style={{ textAlign: "center" }}>
          <span className="eyebrow">Support the Mission</span>
          <h1 className="display-1 page-hero__title-spaced">Give the Gift of Wisdom</h1>
          <p className="body-xl text-muted">
            Aekam is a community-supported platform. Every contribution helps us
            offer free teachings, events, and tools to seekers worldwide.
          </p>
        </div>
      </section>

      {/* Impact metrics */}
      <section className="section section--surface-alt">
        <div className="container">
          <div className="donate-impact-grid">
            <div className="donate-impact-card">
              <div className="donate-impact-card__amount">$11</div>
              <div className="donate-impact-card__label">Covers one seeker's event materials</div>
            </div>
            <div className="donate-impact-card">
              <div className="donate-impact-card__amount">$51</div>
              <div className="donate-impact-card__label">Sponsors a satsang session</div>
            </div>
            <div className="donate-impact-card">
              <div className="donate-impact-card__amount">$108</div>
              <div className="donate-impact-card__label">Funds one month of platform hosting</div>
            </div>
            <div className="donate-impact-card">
              <div className="donate-impact-card__amount">$501</div>
              <div className="donate-impact-card__label">Sponsors a full retreat day</div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation form */}
      <section className="section">
        <div className="container">
          <div className="donate-layout">
            {/* Form */}
            <div className="donate-form-panel">
              <h2 className="section-title" style={{ marginBottom: "1.5rem" }}>Make a Donation</h2>
              <form onSubmit={handleDonate}>
                {/* Amount presets */}
                <div className="form-group">
                  <label className="form-label">Choose an amount</label>
                  <div className="donate-amount-grid">
                    {presetAmounts.map((a) => (
                      <button
                        key={a}
                        type="button"
                        className={`donate-amount-btn${amount === a ? " active" : ""}`}
                        onClick={() => { setAmount(a); setCustomAmount(""); }}
                      >
                        ${a}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="custom-amount">Or enter a custom amount</label>
                  <div className="donate-custom-amount">
                    <span className="donate-currency-symbol">$</span>
                    <input
                      id="custom-amount"
                      type="number"
                      min="1"
                      className="form-input"
                      placeholder="0.00"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setAmount(""); }}
                    />
                  </div>
                </div>

                {/* Payment method */}
                <div className="form-group">
                  <label className="form-label">Payment method</label>
                  <div className="donate-method-grid">
                    <button
                      type="button"
                      className={`donate-method-btn${selectedMethod === "bank" ? " active" : ""}`}
                      onClick={() => setSelectedMethod("bank")}
                    >
                      🏦 Bank Transfer
                    </button>
                    <button
                      type="button"
                      className={`donate-method-btn${selectedMethod === "zelle" ? " active" : ""}`}
                      onClick={() => setSelectedMethod("zelle")}
                    >
                      💸 Zelle (USA)
                    </button>
                    <button
                      type="button"
                      className={`donate-method-btn${selectedMethod === "upi" ? " active" : ""}`}
                      onClick={() => setSelectedMethod("upi")}
                    >
                      📱 UPI (India)
                    </button>
                  </div>
                </div>

                {/* Instructions based on method */}
                {selectedMethod === "bank" && (
                  <div className="donate-instructions">
                    <div className="donate-instructions__label">Bank Wire / Transfer</div>
                    <div className="donate-detail-row">
                      <span>Bank</span>
                      <span>Wells Fargo</span>
                    </div>
                    <div className="donate-detail-row">
                      <span>Account #</span>
                      <button
                        type="button"
                        className="donation-copy-btn"
                        onClick={() => copyToClipboard("3017957717", "acct")}
                      >
                        3017957717 {copied === "acct" ? "✓" : "📋"}
                      </button>
                    </div>
                    {finalAmount > 0 && (
                      <div className="donate-detail-row">
                        <span>Amount</span>
                        <strong>${finalAmount}</strong>
                      </div>
                    )}
                  </div>
                )}

                {selectedMethod === "zelle" && (
                  <div className="donate-instructions">
                    <div className="donate-instructions__label">Zelle Payment</div>
                    <div className="donate-detail-row">
                      <span>Send to</span>
                      <button
                        type="button"
                        className="donation-copy-btn"
                    onClick={() => copyToClipboard("hello@aekam.app", "zelle")}
                      >
                    hello@aekam.app {copied === "zelle" ? "✓" : "📋"}
                      </button>
                    </div>
                    {finalAmount > 0 && (
                      <div className="donate-detail-row">
                        <span>Amount</span>
                        <strong>${finalAmount}</strong>
                      </div>
                    )}
                  </div>
                )}

                {selectedMethod === "upi" && (
                  <div className="donate-instructions">
                    <div className="donate-instructions__label">UPI Payment (India)</div>
                    <div className="donate-detail-row">
                      <span>UPI ID</span>
                      <button
                        type="button"
                        className="donation-copy-btn"
                        onClick={() => copyToClipboard("Wayto97046356@barodampay", "upi")}
                      >
                        Wayto97046356@barodampay {copied === "upi" ? "✓" : "📋"}
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn--brand"
                  style={{ width: "100%", marginTop: "1.5rem" }}
                  disabled={finalAmount <= 0 || !selectedMethod}
                >
                  {finalAmount > 0 ? `Confirm Donation of $${finalAmount}` : "Select Amount to Continue"}
                </button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="donate-sidebar">
              <div className="donate-sidebar-card">
                <div className="donate-sidebar-card__icon">📄</div>
                <h3>Tax Deductible</h3>
                <p className="text-muted">
              Aekam is supported by its community. Receipts available upon request.
                </p>
              </div>
              <div className="donate-sidebar-card">
                <div className="donate-sidebar-card__icon">🌍</div>
                <h3>Worldwide Impact</h3>
                <p className="text-muted">
                  Seekers in over 30 countries access our teachings. Your donation makes
                  the path available to all, regardless of financial means.
                </p>
              </div>
              <div className="donate-sidebar-card">
                <div className="donate-sidebar-card__icon">📞</div>
                <h3>Questions?</h3>
                <p className="text-muted">
              <a href="mailto:hello@aekam.app" className="text-link">
                hello@aekam.app
                  </a>
                  <br />
                  <a href="tel:+19292665742" className="text-link">
                    929-266-5742
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
